import { Logo } from '@/components/logo'
import { OnboardingLayout } from '@/components/onboarding-layout'
import { Button, Group, Field, Notice, PasswordInput } from '@tempots/beatui'
import { attr, Fragment, html, OneOfValue, prop, Use, on } from '@tempots/dom'
import { HTMLTitle, Location } from '@tempots/ui'
import { ApiError, Auth } from '@/services/auth-service'
import { getPasswordErrorMessage } from '@rh/shared'
import { Track } from '@/services/track'

export function ResetPasswordPage() {
  return Fragment(
    HTMLTitle('Reset Password - ResumeHeaders'),
    Use(Location, location => {
      const token = location.search.$.token?.value ?? ''

      if (!token) {
        return OnboardingLayout(
          {},
          html.div(
            attr.class('w-full md:max-w-md'),
            html.div(
              attr.class('flex justify-center mb-6 md:mb-8'),
              Logo({ size: 'main' })
            ),
            html.h2(
              attr.class(
                'text-lg md:text-xl font-bold text-center mb-3 md:mb-4'
              ),
              'Reset link not found'
            ),
            Notice(
              {
                variant: 'danger',
                tone: 'prominent',
                title: 'Missing or invalid token',
              },
              'The reset link may have expired or is missing. Request a new one to continue.'
            ),
            Button(
              {
                variant: 'filled',
                color: 'primary',
                onClick: () => {
                  Track.click({
                    section: 'main',
                    element_type: 'button',
                    click_action: 'navigate',
                    element_id: 'forgot_password',
                  })
                  location.navigate('/forgot-password')
                },
              },
              'Request new reset link'
            ),
            html.div(
              attr.class('mt-8 text-center text-sm text-gray-600'),
              html.button(
                attr.type('button'),
                attr.class('text-teal-600 hover:underline font-medium'),
                on.click(() => {
                  Track.click({
                    section: 'main',
                    element_type: 'button',
                    click_action: 'navigate',
                    element_id: 'signin',
                  })
                  location.navigate('/signin')
                }),
                'Return to Sign in'
              )
            )
          )
        )
      }

      const password = prop('')
      const confirmPassword = prop('')
      const submitting = prop(false)
      const feedbackState = prop<'idle' | 'success' | 'error'>('idle')
      const feedbackMessage = prop<string>('')

      const clearErrorFeedback = () => {
        if (feedbackState.value === 'error') {
          feedbackState.set('idle')
          feedbackMessage.set('')
        }
      }

      const handleSubmit = async () => {
        Track.click({
          section: 'main',
          element_type: 'button',
          click_action: 'submit',
          element_id: 'reset_password',
        })

        const nextPassword = password.value.trim()
        const confirm = confirmPassword.value.trim()

        const passwordError = getPasswordErrorMessage(nextPassword)
        if (passwordError) {
          feedbackState.set('error')
          feedbackMessage.set(passwordError)
          Track.clientError({
            error_code: 'RESET_PASSWORD_INVALID_PASSWORD',
            error_message: passwordError,
          })
          return
        }

        if (nextPassword !== confirm) {
          feedbackState.set('error')
          feedbackMessage.set('Passwords do not match. Please try again.')
          Track.clientError({
            error_code: 'RESET_PASSWORD_PASSWORDS_DONT_MATCH',
            error_message: 'Passwords do not match',
          })
          return
        }

        try {
          submitting.set(true)
          const message = await Auth.resetPassword(token, nextPassword)
          feedbackState.set('success')
          feedbackMessage.set(
            message || 'Your password has been updated. You can now sign in.'
          )
        } catch (error) {
          feedbackState.set('error')
          if (error instanceof ApiError) {
            feedbackMessage.set(
              error.message ||
                'We could not reset your password right now. Please try again later.'
            )
          } else {
            feedbackMessage.set(
              'We could not reset your password right now. Please try again later.'
            )
          }
        } finally {
          submitting.set(false)
        }
      }

      return OnboardingLayout(
        {},
        html.div(
          attr.class('w-full md:max-w-md'),
          html.div(
            attr.class('flex justify-center mb-6 md:mb-8'),
            Logo({ size: 'main' })
          ),
          html.h2(
            attr.class('text-lg md:text-xl font-bold text-center mb-3 md:mb-4'),
            'Set a new password'
          ),
          html.p(
            attr.class('text-center text-gray-600 mb-6 text-sm md:text-base'),
            'Choose a new password below. You will use this to sign in next time.'
          ),
          html.form(
            attr.class('flex flex-col gap-4'),
            on.submit(event => {
              event.preventDefault()
              void handleSubmit()
            }),
            Field({
              label: 'New password',
              content: PasswordInput({
                value: password,
                placeholder: 'Enter your new password',
                id: 'password-input',
                onInput: v => {
                  password.set(v)
                  clearErrorFeedback()
                },
              }),
            }),
            Field({
              label: 'Confirm new password',
              content: PasswordInput({
                value: confirmPassword,
                placeholder: 'Re-enter your new password',
                id: 'confirm-password-input',
                onInput: v => {
                  confirmPassword.set(v)
                  clearErrorFeedback()
                },
              }),
            }),
            html.div(
              attr.id('submit-button'),
              Button(
                {
                  type: 'submit',
                  variant: 'filled',
                  color: 'primary',
                  loading: submitting,
                  disabled: submitting,
                },
                Group(
                  attr.class('items-center gap-2 justify-center'),
                  html.span('Reset password')
                )
              )
            ),
            OneOfValue(feedbackState, {
              success: () =>
                html.div(
                  attr.class('flex flex-col gap-4'),
                  html.div(
                    attr.id('success-message'),
                    Notice(
                      {
                        variant: 'success',
                        tone: 'prominent',
                        title: 'Password updated',
                      },
                      feedbackMessage.map(message => message ?? '')
                    )
                  ),
                  Button(
                    {
                      variant: 'outline',
                      color: 'primary',
                      onClick: () => location.navigate('/signin'),
                    },
                    'Continue to sign in'
                  )
                ),
              error: () =>
                html.div(
                  attr.id('error-message'),
                  Notice(
                    {
                      variant: 'danger',
                      tone: 'prominent',
                      title: 'Unable to reset password',
                    },
                    feedbackMessage.map(message => message ?? '')
                  )
                ),
              idle: () => html.span(),
            })
          ),
          html.div(
            attr.class(
              'mt-8 flex flex-col gap-2 text-center text-sm text-gray-600'
            ),
            html.span(
              'Changed your mind? ',
              html.button(
                attr.type('button'),
                attr.class('text-teal-600 hover:underline font-medium'),
                on.click(() => {
                  Track.click({
                    section: 'main',
                    element_type: 'button',
                    click_action: 'navigate',
                    element_id: 'signin',
                  })
                  location.navigate('/signin')
                }),
                'Back to Sign in'
              )
            ),
            html.span(
              'Need a new link? ',
              html.button(
                attr.type('button'),
                attr.class('text-teal-600 hover:underline font-medium'),
                on.click(() => {
                  Track.click({
                    section: 'main',
                    element_type: 'button',
                    click_action: 'navigate',
                    element_id: 'forgot_password',
                  })
                  location.navigate('/forgot-password')
                }),
                'Request another reset email'
              )
            ),
            html.span(
              'New to ResumeHeaders? ',
              html.button(
                attr.type('button'),
                attr.class('text-teal-600 hover:underline font-medium'),
                on.click(() => {
                  Track.click({
                    section: 'main',
                    element_type: 'button',
                    click_action: 'navigate',
                    element_id: 'signup',
                  })
                  location.navigate('/signup')
                }),
                'Create an account'
              )
            )
          )
        )
      )
    })
  )
}
